/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useState, useEffect, useRef } from "react";
import profilePhoto from "../../assets/profile_photo.svg";
import HelmetLayout from "../../components/HelmetLayout";
import { APIContext } from "../../api/api";
import { PulseLoader } from "react-spinners";
import {
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Text,
  Box,
} from "@chakra-ui/react";
import slugify from "slugify";

function Webinars() {
  const { listOfAllWebinars, createNewWebinar, updateWebinar, deleteWebinar, getWebinar } = useContext(APIContext);
  const [webinarList, setWebinarList] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [webinarTitle, setWebinarTitle] = useState("");
  const [webinarDescription, setWebinarDescription] = useState("");
  const [webinarSlug, setWebinarSlug] = useState("");
  const [webinarThumbnail, setWebinarThumbnail] = useState("");
  const [webinarThumbnailFile, setWebinarThumbnailFile] = useState(null);
  const [webinarYoutubeLink, setWebinarYoutubeLink] = useState("");
  const [webinarScheduledTime, setWebinarScheduledTime] = useState("");

  const thumbnailRef = useRef(null);

  useEffect(() => {
    refreshList();
  }, []);

  const refreshList = () => {
    listOfAllWebinars()
      .then((list) => {
        setWebinarList(list);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const openAddModal = () => {
    setEditingId(null);
    setWebinarTitle("");
    setWebinarDescription("");
    setWebinarSlug("");
    setWebinarThumbnail("");
    setWebinarThumbnailFile(null);
    setWebinarYoutubeLink("");
    setWebinarScheduledTime("");
    onOpen();
  };

  const openEditModal = (webinar) => {
    const id = webinar.pk.replace("webinar-", "");
    setEditingId(id);
    // If we need fresh data
    getWebinar(id).then((data) => {
      setWebinarTitle(data.webinar_title || "");
      setWebinarDescription(data.webinar_description || "");
      setWebinarSlug(data.webinar_slug || "");
      setWebinarThumbnail(data.webinar_thumbnail || "");
      setWebinarYoutubeLink(data.webinar_youtube_link || "");
      setWebinarScheduledTime(
        data.webinar_scheduled_time ? String(data.webinar_scheduled_time) : ""
      );
      setWebinarThumbnailFile(null);
      onOpen();
    }).catch(() => {
      // fallback to provided webinar data if API fails
      setWebinarTitle(webinar.webinar_title || "");
      setWebinarDescription(webinar.webinar_description || "");
      setWebinarSlug(webinar.webinar_slug || "");
      setWebinarThumbnail(webinar.webinar_thumbnail || "");
      setWebinarYoutubeLink(webinar.webinar_youtube_link || "");
      setWebinarScheduledTime(
        webinar.webinar_scheduled_time ? String(webinar.webinar_scheduled_time) : ""
      );
      setWebinarThumbnailFile(null);
      onOpen();
    });
  };

  const handleSave = () => {
    if (isSaving) return;
    setIsSaving(true);

    if (!editingId) {
      createNewWebinar(
        webinarTitle,
        webinarDescription,
        webinarThumbnailFile,
        webinarYoutubeLink,
        webinarSlug,
        webinarScheduledTime ? parseInt(webinarScheduledTime) : undefined,
      )
        .then(() => {
          setIsSaving(false);
          onClose();
          refreshList();
        })
        .catch(() => setIsSaving(false));
    } else {
      updateWebinar(
        editingId,
        webinarTitle,
        webinarDescription,
        webinarThumbnailFile,
        webinarYoutubeLink,
        webinarSlug,
        webinarScheduledTime ? parseInt(webinarScheduledTime) : undefined,
      )
        .then(() => {
          setIsSaving(false);
          onClose();
          refreshList();
        })
        .catch(() => setIsSaving(false));
    }
  };

  const handleDelete = () => {
    if (!editingId || isDeleting) return;
    setIsDeleting(true);
    deleteWebinar(editingId)
      .then(() => {
        setIsDeleting(false);
        onClose();
        refreshList();
      })
      .catch(() => setIsDeleting(false));
  };

  return (
    <>
      <HelmetLayout title="Webinars" />
      {!loading && (
        <div className="font-Inter flex-1 h-screen overflow-y-scroll bg-white items-start p-7">
          <div className="flex flex-col items-center justify-start">
            <div className="w-full flex flex-row justify-between mb-6">
              <h1 className="text-[32px]">Webinars</h1>
              <img
                src={profilePhoto}
                className="mr-4 h-9"
                alt="user-profile-img"
              />
            </div>

            {/* Card Section */}

            <div className="w-full flex justify-start items-center m-5 ml-0 gap-[14px]">
              <div className="rounded-[29px] shadow-card-box-shadow">
                <div className="w-[255px] h-[100px] flex flex-col justify-center items-center">
                  <div className="flex flex-col items-start">
                    <h1 className="font-bold">{webinarList.length}</h1>
                    <p>Total Webinars</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Add New Webinar Button */}

            <div className="w-full flex justify-between items-center">
              <h1 className="text-[#121213] text-2xl">All Webinars</h1>
              <div className="flex gap-[10px]">
                <button onClick={openAddModal} className="rounded-[20px] bg-[#F5F7FC] px-4 py-3 items-center text-[14px] text-center">
                  Add New
                </button>
              </div>
            </div>

            {/* Webinars List Section */}

            <div className="w-full flex flex-row flex-wrap justify-start items-center gap-5 mt-10">
              {webinarList.map((webinar) => {
                const webinarID = webinar.pk.replace("webinar-", "");
                return (
                  <div
                    key={webinarID}
                    className="flex flex-col"
                    title={webinar.webinar_title}
                  >
                    <div className="relative">
                      <img
                        className="w-[250px] h-[200px] rounded-[9px] object-cover"
                        src={webinar.webinar_thumbnail}
                        alt="webinar-thumbnail"
                      />
                    </div>
                    <div className="flex items-center mt-2 mb-4 justify-between">
                      {webinar.webinar_title}
                      <button
                        onClick={() => openEditModal(webinar)}
                        className="inline-block ml-2 text-sm rounded-[14px] bg-[#F5F7FC] px-3 py-1"
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Webinar Modal */}
      <Modal blockScrollOnMount={false} isOpen={isOpen} onClose={onClose} borderRadius="20px">
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader mt="20px" textAlign="center" fontSize="24px" fontWeight="medium">
            {editingId ? "Edit Webinar" : "New Webinar"}
          </ModalHeader>
          <ModalBody>
            <div className="w-full flex flex-col justify-start p-1 gap-y-5">
              <div>
                <label htmlFor="title" className="block text-lg text-[#121212] font-medium">
                  Title
                </label>
                <input
                  value={webinarTitle}
                  onChange={(e) => setWebinarTitle(e.target.value)}
                  className="block mt-2 w-full placeholder-gray-400/70 rounded-3xl bg-white px-5 py-2.5 text-gray-700 shadow-input-tag"
                />
              </div>
              <div>
                <label htmlFor="description" className="block text-lg text-[#121212] font-medium">
                  Description
                </label>
                <textarea
                  rows="6"
                  value={webinarDescription}
                  onChange={(e) => setWebinarDescription(e.target.value)}
                  className="block mt-2 w-full placeholder-gray-400/70 rounded-3xl bg-white px-5 py-2.5 text-gray-700 shadow-input-tag"
                />
              </div>
              <div>
                <label htmlFor="youtube" className="block text-lg text-[#121212] font-medium">
                  YouTube Video Link
                </label>
                <input
                  value={webinarYoutubeLink}
                  onChange={(e) => setWebinarYoutubeLink(e.target.value)}
                  className="block mt-2 w-full placeholder-gray-400/70 rounded-3xl bg-white px-5 py-2.5 text-gray-700 shadow-input-tag"
                />
              </div>
              <div>
                <label htmlFor="scheduled_time" className="block text-lg text-[#121212] font-medium">
                  Scheduled Date & Time (UTC)
                </label>
                <input
                  type="datetime-local"
                  value={webinarScheduledTime ? new Date(parseInt(webinarScheduledTime) * 1000).toISOString().slice(0,16) : ""}
                  onChange={(e) => {
                    const dt = e.target.value; // yyyy-MM-ddTHH:mm
                    if (!dt) {
                      setWebinarScheduledTime("");
                      return;
                    }
                    const timestamp = Math.floor(new Date(dt).getTime() / 1000);
                    setWebinarScheduledTime(String(timestamp));
                  }}
                  className="block mt-2 w-full placeholder-gray-400/70 rounded-3xl bg-white px-5 py-2.5 text-gray-700 shadow-input-tag"
                />
              </div>
              <div>
                <label htmlFor="slug" className="block text-lg text-[#121212] font-medium">
                  Slug
                </label>
                <input
                  value={webinarSlug}
                  onChange={(e) => setWebinarSlug(slugify(e.target.value, {
                    trim: false,
                    strict: true,
                    lower: true,
                  }))}
                  className="block mt-2 w-full placeholder-gray-400/70 rounded-3xl bg-white px-5 py-2.5 text-gray-700 shadow-input-tag"
                />
              </div>
              <div>
                <label htmlFor="thumbnail" className="block text-lg text-[#121212] font-medium">
                  Thumbnail
                </label>
                {webinarThumbnail && (
                  <div>
                    <img src={webinarThumbnail} alt="webinar-thumbnail" className="w-[200px] h-[200px] object-cover rounded-3xl" />
                  </div>
                )}
                <div className="flex flex-row gap-2 items-center mt-2">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      thumbnailRef.current.click();
                    }}
                    className="px-4 py-2 text-center text-[16px] text-[#5CBD20] font-medium rounded-[20px] shadow-card-box-shadow border border-solid border-[#5CBD20]"
                  >
                    {webinarThumbnail ? "Replace Thumbnail" : "Add Thumbnail"}
                  </button>
                </div>
                <input
                  ref={thumbnailRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files[0];
                    const reader = new FileReader();
                    reader.readAsDataURL(file);
                    reader.onloadend = () => {
                      setWebinarThumbnail(reader.result);
                    };
                    setWebinarThumbnailFile(file);
                  }}
                />
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Box width="100%" display="flex" justifyContent="space-between" alignItems="center">
              {editingId && (
                <button
                  onClick={handleDelete}
                  className="px-3 py-[9px] text-left text-[16px] text-[#DE1111] font-medium rounded-[20px] shadow-card-box-shadow border border-solid border-[#DE1111]"
                >
                  {isDeleting ? <PulseLoader size={8}></PulseLoader> : "Delete"}
                </button>
              )}
              <button
                onClick={handleSave}
                className="px-3 py-[9px] text-left text-[16px] text-[#5CBD20] font-medium rounded-[20px] shadow-card-box-shadow border border-solid border-[#5CBD20]"
              >
                {isSaving ? <PulseLoader size={8}></PulseLoader> : editingId ? "Update" : "Create"}
              </button>
            </Box>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default Webinars;
